import React from 'react';
import get from 'lodash/get';

import Element from '~/src/components/element/Element';
import PublishDate from '~/src/components/publishDate/PublishDate';
import TeaserAction from '~/src/components/teaser/teaserAction/TeaserAction';
import ActionBubble from '~/src/components/actionBubble/ActionBubble';
import Boomerang from '~/src/components/svg/general/Boomerang';
import Link from '~/src/components/link/Link';
import VideoPlayer from '~/src/components/video/player/VideoPlayer';
import VideoSerie from '~/src/components/content/video/videoSerie/VideoSerie';
import EmbedIframe from '~/src/components/embed/iframe/EmbedIframe';
import TypeContext from '~/src/components/context/TypeContext';
import RatingOverlay from '~/src/components/rating/overlay/RatingOverlay';

import {withSitemapContext} from '~/src/components/context/SitemapContext';
import {getCategoryUrl, getVideoUrl} from '~/src/utilities/helpers/link';
import {ElementTypes, TeaserTypes} from '~/src/utilities/constants/types';
import {pushRoute} from '~/src/utilities/helpers/router';
import initPolyglot from '~/src/utilities/libs/polyglot';
import {Hashes} from '~/src/utilities/constants/hash';
import {onRating} from '~/src/utilities/helpers/rating';

import css from './VideoContent.module.scss';
import i18nStrings from './VideoContent.i18n.json';

const getTranslation = initPolyglot(i18nStrings);

const supportedTypes = [ElementTypes.VIDEOCMS, ElementTypes.LIVESTREAM];

class VideoContent extends React.Component {
    isSeries() {
        return this.props.series && this.props.series.length > 0;
    }

    getNextVideo() {
        if (this.isSeries()) {
            const currentIndex = this.props.series.findIndex((video) => video.id.toString() === this.props.id);
            return this.props.series.find((video, index) => index === currentIndex + 1);
        }

        if (this.props.relatedContent) {
            const currentIndex = this.props.relatedContent.elements.findIndex((video) => video.id.toString() === this.props.id);
            return this.props.relatedContent.elements.find((video, index) => index === currentIndex + 1);
        }

        return null;
    }

    onComplete = () => {
        const nextVideo = this.getNextVideo();
        if (nextVideo && nextVideo.type === TeaserTypes.VIDEOS) {
            window.setTimeout(pushRoute, 100, this.props.context.sitemap.all, getVideoUrl(nextVideo.content.url));
        }
    };

    toggleRatingOverlay = () => {
        if (this.state.showRatingOverlay) {
            pushRoute(this.props.context.sitemap.all, getVideoUrl(this.props.content.meta.url), true);
        }

        this.setState({showRatingOverlay: !this.state.showRatingOverlay});
    };

    getVideo() {
        const [video] = get(this.props, 'content.video.content.elements').filter((element) => supportedTypes.includes(element.type));
        return video;
    }

    getVideoElement() {
        const video = this.getVideo();
        const metaData = {
            meta: get(this.props, 'content.meta')
        };

        if (video && video.type) {
            switch (video.type) {
                case ElementTypes.VIDEOCMS:
                    return (
                        <VideoPlayer
                            {...video.content.elements[0]}
                            content={metaData}
                            position={this.props.position}
                            autoplay={!this.props.isFirstPageRequest}
                            events={{onComplete: this.onComplete}}
                            fallbackUrl={video.url}
                        />
                    );
                case ElementTypes.LIVESTREAM:
                    return <EmbedIframe {...video} />;
                default:
                    return null;
            }
        }
        return null;
    }

    onRatingAction = onRating({
        id: this.getVideo()?.content?.elements[0]?.video_id || this.props.id,
        title: this.props.content.video.title,
        authors: this.props.content.meta.authors,
        fullUrlPath: this.props.content.meta.mainCategoryFullUrlPath,
        type: this.props.type
    });

    state = {
        showRatingOverlay: false
    };

    componentDidMount() {
        if (window.location.hash === `#${Hashes.RATING_SECTION}`) {
            this.toggleRatingOverlay();
        }
    }

    render() {
        const {titleHeader, title, lead} = this.props.content.video;
        const {categories = []} = this.props.content.meta;
        const {categoryId: mainCategory, name: mainCategoryName, fullUrlPath: mainCategoryFullUrlPath} =
            categories.find((categoryId) => this.props.context.sitemap.getCategoryBy({categoryId})) || {};

        return (
            <TypeContext value={this.props.type}>
                <article className={css.article}>
                    <section className={css.video}>{this.getVideoElement()}</section>
                    <section className={css.info}>
                        <header className={css.header}>
                            <div className={css.headerTop}>
                                {this.props.seriesName && <h2 className={css.seriesTitle}>{this.props.seriesName}</h2>}
                                <PublishDate publishedTime={get(this.props, 'content.meta.published')} />
                            </div>

                            {title && (
                                <h1 className={css.title}>
                                    {titleHeader && (
                                        <span className={css.titleHeader}>
                                            {titleHeader}
                                            <span className="visually-hidden" aria-hidden="true" role="presentation">
                                                :{' '}
                                            </span>
                                        </span>
                                    )}
                                    {title}
                                </h1>
                            )}

                            {lead && (
                                <div className={css.lead}>
                                    {lead.elements.map((element, index) => (
                                        <Element key={'lead_' + index} {...element} />
                                    ))}
                                </div>
                            )}
                        </header>
                        <footer className={css.footer}>
                            <TeaserAction
                                class={css.teaserAction}
                                id={this.props.id}
                                likes={get(this.props, 'content.meta.clientdata.community.likes')}
                                comments={get(this.props, 'content.meta.clientdata.community.comments')}
                                shares={get(this.props, 'content.meta.clientdata.community.shares')}
                                contentRatings={get(this.props, 'content.meta.clientdata.community.contentRatings')}
                                settings={get(this.props, 'content.meta.settings')}
                                showNumbers={true}
                                onRatingClick={this.toggleRatingOverlay}
                                format={this.props.format}
                            />
                            <ActionBubble
                                metadataId={this.props.id}
                                settings={get(this.props, 'content.meta.settings')}
                                onRatingClick={this.toggleRatingOverlay}
                            />
                        </footer>
                    </section>
                    {this.isSeries() && (
                        <section className={css.videoSeries}>
                            <VideoSerie seriesName={this.props.seriesName} videos={this.props.series} currentVideoId={this.props.id} />
                        </section>
                    )}
                    <section className={css.links}>
                        {mainCategory && (
                            <Link className={css.link} href={getCategoryUrl(mainCategoryFullUrlPath)}>
                                {getTranslation('toCategory', {channel: mainCategoryName})}
                                <Boomerang />
                            </Link>
                        )}
                    </section>
                    {this.state.showRatingOverlay && (
                        <RatingOverlay metadataId={this.props.id} onRating={this.onRatingAction} onClose={this.toggleRatingOverlay} />
                    )}
                </article>
            </TypeContext>
        );
    }
}

export default withSitemapContext(VideoContent);
